import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuthContext } from "@/contexts/AuthContext";
import { ProfileTabsProvider } from "@/contexts/ProfileTabsContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { LoginPage } from "@/components/auth/LoginPage";
import { ChangePasswordModal } from "@/components/auth/ChangePasswordModal";
import Index from "./pages/Index";
import Projects from "./pages/Projects";
import ApiTesting from "./pages/ApiTesting";
import CompanyDashboard from "./pages/CompanyDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminStats from "./pages/admin/AdminStats";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const AppContent = () => {
  const { isAuthenticated, isLoading, user, login, forceLogin, changePassword } = useAuthContext();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Ładowanie...</div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated || !user) {
    return <LoginPage onLogin={login} onForceLogin={forceLogin} />;
  }

  // Must change password
  if (user.must_change_password) {
    return (
      <>
        <ChangePasswordModal
          isOpen={true}
          isFirstLogin={true}
          onChangePassword={changePassword}
        />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-muted-foreground">Zmień hasło, aby kontynuować...</div>
        </div>
      </>
    );
  }

  return (
    <Routes>
      {/* Public routes (for authenticated users) */}
      <Route path="/" element={<Index />} />
      
      {/* Company Admin & Super Admin routes */}
      <Route 
        path="/projects" 
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
            <Projects />
          </ProtectedRoute>
        } 
      />
      
      {/* Company Admin dashboard */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute allowedRoles={['company_admin']}>
            <CompanyDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Super Admin only routes */}
      <Route 
        path="/api-testing" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ApiTesting />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin panel routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/companies" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminCompanies />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminUsers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/stats" 
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminStats />
          </ProtectedRoute>
        } 
      />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <ProfileTabsProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppContent />
          </TooltipProvider>
        </ProfileTabsProvider>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
